/**
 * Pure verdict-sentence composition for `/admin/automation/workloads`,
 * split out of `WorkloadsSections.tsx` so it can be exported alongside pure
 * helpers without tripping `react-refresh/only-export-components` (that file
 * is component-exports-only, per the project's `--max-warnings 0` lint gate)
 * — same reason `quota-display.ts` / `phases.ts` sit beside their sections in
 * this directory rather than living inside the `.tsx`.
 *
 * `WorkloadsStatus.verdict` on the wire is a bare `'red' | 'amber' | 'green'`
 * with no server-authored reason string (unlike `AddisSystemStatus.reason`),
 * so this is where "1 exile, ~$480/mo" or "no findings — every job landed
 * where doctrine says" comes from.
 */
import type { WorkloadsStatus } from '@a7y/types';
import { fmtLocal } from './shared';

function pluralize(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? '' : 's'}`;
}

function fmtMonthlyUsd(v: number): string {
  return `~$${Math.round(v).toLocaleString('en-US')}/mo`;
}

/**
 * Compose the banner sentence from `findings` + `routing` + `stale`.
 * Exported for direct unit testing and for the page's error/loading callers.
 */
export function composeVerdictSentence(status: WorkloadsStatus): string {
  const exiles = status.findings.filter((f) => f.kind === 'EXILE');
  const doctrineViolations = status.findings.filter((f) => f.kind === 'DOCTRINE_VIOLATION');
  const splits = status.findings.filter((f) => f.kind === 'SPLIT_PLACEMENT');

  if (status.verdict === 'red') {
    const parts: string[] = [];
    if (exiles.length > 0) {
      const cost = exiles.reduce((sum, f) => sum + (f.estMonthlyUsd ?? 0), 0);
      parts.push(
        cost > 0
          ? `${pluralize(exiles.length, 'exile')}, ${fmtMonthlyUsd(cost)}`
          : pluralize(exiles.length, 'exile')
      );
    }
    if (doctrineViolations.length > 0) {
      parts.push(
        `${pluralize(doctrineViolations.length, 'doctrine violation')} — a KEEP-covered job landed on UKKO`
      );
    }
    return parts.length > 0 ? parts.join(' · ') : 'A red-grade finding was reported.';
  }

  if (status.verdict === 'amber') {
    const parts: string[] = [];
    if (!status.producedAt) {
      parts.push('the daily producer has not run yet');
    } else if (status.stale) {
      parts.push(`workload data is stale (last produced ${fmtLocal(status.producedAt)})`);
    }
    if (status.unreadable.length > 0) {
      parts.push(`${pluralize(status.unreadable.length, 'source')} could not be read`);
    }
    const overflow = status.routing.filter((r) => r.status === 'off-pool-overflow');
    if (overflow.length > 0) {
      parts.push(`overflow routing ${overflow.map((r) => r.class).join(', ')}`);
    }
    const failover = status.routing.filter((r) => r.status === 'off-pool-failover');
    if (failover.length > 0) parts.push(`failover active for ${pluralize(failover.length, 'class')}`);
    const disabled = status.routing.filter((r) => r.status === 'off-pool-disabled');
    if (disabled.length > 0) parts.push(`${pluralize(disabled.length, 'class')} disabled by an operator`);
    const absentUnexpected = status.routing.filter((r) => r.status === 'absent-unexpected');
    if (absentUnexpected.length > 0) {
      parts.push(`${pluralize(absentUnexpected.length, 'class')} missing an expected variable`);
    }
    const exiledNoEvidence = status.routing.filter((r) => r.status === 'off-pool-exile');
    if (exiledNoEvidence.length > 0 && exiles.length === 0) {
      parts.push(`${pluralize(exiledNoEvidence.length, 'class')} off-pool with no pool evidence yet`);
    }
    if (splits.length > 0) parts.push(pluralize(splits.length, 'unexplained split'));
    return parts.length > 0 ? parts.join('; ') : 'Needs review.';
  }

  return 'No findings — every job landed where doctrine says.';
}
